/**
 * Onboarding Service – real API only.
 */

import { serviceLogger } from '../utils/serviceLogger';
import { normalizeServiceError } from '../utils/serviceError';
import * as realImpl from './real/onboardingService.real';

export const getOnboardingStatus = async () => {
  try {
    return await realImpl.getOnboardingStatus();
  } catch (error) {
    serviceLogger.error('[getOnboardingStatus] Error:', error);
    throw normalizeServiceError(error);
  }
};

export const getWizardStatus = async (organizationId) => {
  try {
    const { getFacilities } = await import('./facilitiesService');
    const { getLocationsByFacility } = await import('./locationsService');
    const { getUsersByOrganization } = await import('./userService');

    const facilitiesResponse = await getFacilities({ page: 1, limit: 100 });
    const facilities = (facilitiesResponse?.data || []).filter(
      (facility) => facility.organizationId === organizationId,
    );

    let totalLocations = 0;
    if (facilities.length > 0) {
      const locationChecks = await Promise.all(
        facilities.map(async (facility) => {
          try {
            const response = await getLocationsByFacility(facility.id);
            return response?.data || [];
          } catch (error) {
            serviceLogger.warn(`Could not load locations for facility ${facility.id}:`, error);
            return [];
          }
        }),
      );
      totalLocations = locationChecks.reduce((sum, locations) => sum + locations.length, 0);
    }

    const users = await getUsersByOrganization(organizationId);
    const facilitiesWithStandards = facilities.filter(
      (f) => f.cleaningStandards && Object.keys(f.cleaningStandards).length > 0,
    ).length;

    const step1 = {
      step: 1,
      isEnabled: true,
      isCompleted: facilities.length > 0 && totalLocations > 0,
      stats: { facilitiesCount: facilities.length, locationsCount: totalLocations },
    };
    const step2Enabled = facilities.length > 0 && totalLocations > 0;
    const step2 = {
      step: 2,
      isEnabled: step2Enabled,
      isCompleted: users.length > 0,
      stats: {
        usersCount: users.length,
        facilitiesCount: facilities.length,
        locationsCount: totalLocations,
      },
    };
    const step3Enabled = facilities.length > 0 && totalLocations > 0;
    const step3 = {
      step: 3,
      isEnabled: step3Enabled,
      isCompleted: facilitiesWithStandards > 0,
      stats: {
        facilitiesCount: facilities.length,
        standardsConfigured: facilitiesWithStandards,
        usersCount: users.length,
      },
    };

    return { steps: [step1, step2, step3] };
  } catch (error) {
    serviceLogger.error('Error getting wizard status:', error);
    return {
      steps: [
        { step: 1, isEnabled: true, isCompleted: false },
        { step: 2, isEnabled: false, isCompleted: false },
        { step: 3, isEnabled: false, isCompleted: false },
      ],
    };
  }
};

export const completeWizardStep = async () => {
  // No-op; step completion is implicit via data state.
};
